import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAuthHeaders } from '../../utils/authHeaders'

const API_URL = process.env.REACT_APP_BACKEND_URL

const DEFAULT_FORM = {
  offer_type: 'school_fg',
  // school_fg fields
  school_name: '',
  school_email: '',
  billing_email: '',
  contact_name: '',
  // individual_t9 fields
  target_user_email: '',
  school_year: '2026/2027',
  // common
  custom_price_eur: '',
  currency: 'EUR',
  valid_until: '',
  notes: ''
}

const AdminCustomOffers = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState(DEFAULT_FORM)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [createdOffer, setCreatedOffer] = useState(null)

  const isIndividual = formData.offer_type === 'individual_t9'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setCreatedOffer(null)

    try {
      const authHeaders = await getAuthHeaders()
      if (!authHeaders.Authorization) {
        throw new Error('Chýba prihlásená relácia. Prosím prihláste sa znova.')
      }

      // Build payload per offer_type — never send school fields for individual_t9.
      const payload = {
        offer_type: formData.offer_type,
        custom_price_eur: parseFloat(formData.custom_price_eur),
        currency: formData.currency,
        valid_until: formData.valid_until,
        notes: formData.notes || null,
      }

      if (isIndividual) {
        payload.target_user_email = formData.target_user_email
        payload.school_year = formData.school_year
      } else {
        payload.school_name = formData.school_name
        payload.school_email = formData.school_email
        payload.billing_email = formData.billing_email || null
        payload.contact_name = formData.contact_name || null
      }

      const response = await fetch(`${API_URL}/api/admin/school-custom-offers`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || 'Nepodarilo sa vytvoriť ponuku')
      }

      const data = await response.json()
      setCreatedOffer(data)

      // Reset form (preserve offer_type so admin can create another of same type).
      setFormData({ ...DEFAULT_FORM, offer_type: formData.offer_type })
    } catch (err) {
      console.error('Error creating custom offer:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    alert('Skopírované do schránky!')
  }

  const getFullOfferUrl = (offer) => {
    const baseUrl = window.location.origin
    const path = offer.offer_type === 'individual_t9'
      ? `/individual-offer/${offer.token}`
      : `/school-offer/${offer.token}`
    return `${baseUrl}${path}`
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="mb-8">
            <button
              data-testid="admin-offers-back-btn"
              onClick={() => navigate('/admin')}
              className="text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-2"
            >
              ← Späť na Admin Dashboard
            </button>
            <h1 className="text-3xl font-bold text-gray-900">
              Vytvoriť Custom Ponuku
            </h1>
            <p className="text-gray-600 mt-2">
              Vytvorte jednorazovú ponuku s vlastnou cenou — buď pre školu (finančná gramotnosť), alebo pre jedného používateľa (Testovanie 9).
            </p>
          </div>

          {/* Success Message */}
          {createdOffer && (
            <div data-testid="admin-offer-success" className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg mb-6">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">✅</span>
                <div>
                  <h3 className="text-lg font-bold text-green-900">Ponuka vytvorená!</h3>
                  <p className="text-sm text-green-700">
                    {createdOffer.offer_type === 'individual_t9'
                      ? `Individuálna ponuka pre ${createdOffer.target_user_email || 'používateľa'} bola úspešne vytvorená`
                      : `Ponuka pre ${createdOffer.school_name} bola úspešne vytvorená`}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded border border-green-200 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {createdOffer.offer_type === 'individual_t9' ? (
                    <>
                      <div><span className="font-semibold">Používateľ:</span> {createdOffer.target_user_email || '—'}</div>
                      <div><span className="font-semibold">Školský rok:</span> {createdOffer.school_year}</div>
                      <div><span className="font-semibold">Produkt:</span> Testovanie 9</div>
                      <div><span className="font-semibold">Cena:</span> {createdOffer.custom_price_eur} {createdOffer.currency}</div>
                      <div className="col-span-2"><span className="font-semibold">Platné do:</span> {new Date(createdOffer.valid_until).toLocaleDateString('sk-SK')}</div>
                    </>
                  ) : (
                    <>
                      <div><span className="font-semibold">Škola:</span> {createdOffer.school_name}</div>
                      <div><span className="font-semibold">Cena:</span> {createdOffer.custom_price_eur} {createdOffer.currency}</div>
                      <div><span className="font-semibold">Email:</span> {createdOffer.school_email}</div>
                      <div><span className="font-semibold">Platné do:</span> {new Date(createdOffer.valid_until).toLocaleDateString('sk-SK')}</div>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <h4 className="font-bold text-gray-900 mb-2">📎 Jedinečný odkaz ponuky:</h4>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    data-testid="admin-offer-url"
                    type="text"
                    value={getFullOfferUrl(createdOffer)}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded font-mono text-sm"
                  />
                  <button
                    data-testid="admin-offer-copy-btn"
                    onClick={() => copyToClipboard(getFullOfferUrl(createdOffer))}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
                  >
                    Skopírovať odkaz ponuky
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  ⚠️ Odkaz je platný iba jedenkrát a do {new Date(createdOffer.valid_until).toLocaleDateString('sk-SK')}.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div data-testid="admin-offer-error" className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg mb-6">
              <div className="flex items-center">
                <span className="text-2xl mr-3">❌</span>
                <div>
                  <h3 className="font-bold text-red-900">Chyba</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Offer Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Typ ponuky *
                </label>
                <select
                  data-testid="admin-offer-type-select"
                  name="offer_type"
                  value={formData.offer_type}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="school_fg">Finančná gramotnosť – škola</option>
                  <option value="individual_t9">Testovanie 9 – individuálna ponuka</option>
                </select>
              </div>

              {/* --- SCHOOL FG branch (legacy, unchanged shape) --- */}
              {!isIndividual && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Názov školy *
                    </label>
                    <input
                      data-testid="admin-offer-school-name-input"
                      type="text"
                      name="school_name"
                      value={formData.school_name}
                      onChange={handleChange}
                      required
                      placeholder="napr. Základná škola Bratislava"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email školy *
                    </label>
                    <input
                      data-testid="admin-offer-school-email-input"
                      type="email"
                      name="school_email"
                      value={formData.school_email}
                      onChange={handleChange}
                      required
                      placeholder="napr. skola@example.sk"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Používateľ s týmto emailom bude môcť ponuku použiť
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Fakturačný email (nepovinné)
                    </label>
                    <input
                      type="email"
                      name="billing_email"
                      value={formData.billing_email}
                      onChange={handleChange}
                      placeholder="napr. faktury@example.sk"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Kontaktná osoba (nepovinné)
                    </label>
                    <input
                      type="text"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      placeholder="napr. Mgr. Jana Nováková"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </>
              )}

              {/* --- INDIVIDUAL T9 branch --- */}
              {isIndividual && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Používateľ (email) *
                    </label>
                    <input
                      data-testid="admin-offer-target-user-email-input"
                      type="email"
                      name="target_user_email"
                      value={formData.target_user_email}
                      onChange={handleChange}
                      required
                      placeholder="napr. student@example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Zadajte email existujúceho EduMisia používateľa. Ponuku bude môcť použiť iba on.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Školský rok *
                    </label>
                    <input
                      data-testid="admin-offer-school-year-input"
                      type="text"
                      name="school_year"
                      value={formData.school_year}
                      onChange={handleChange}
                      required
                      placeholder="napr. 2026/2027"
                      pattern="\d{4}/\d{4}"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Formát: RRRR/RRRR
                    </p>
                  </div>
                </>
              )}

              {/* --- COMMON --- */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Vlastná cena *
                  </label>
                  <input
                    data-testid="admin-offer-price-input"
                    type="number"
                    name="custom_price_eur"
                    value={formData.custom_price_eur}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                    placeholder={isIndividual ? 'napr. 9.99' : 'napr. 199.00'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mena
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="CZK">CZK</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Platné do *
                </label>
                <input
                  data-testid="admin-offer-valid-until-input"
                  type="datetime-local"
                  name="valid_until"
                  value={formData.valid_until}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Po tomto dátume nebude možné ponuku použiť
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Poznámky (nepovinné)
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Interné poznámky o ponuke..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  data-testid="admin-offer-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                >
                  {loading ? 'Vytvára sa...' : 'Vytvoriť Ponuku'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
                >
                  Zrušiť
                </button>
              </div>
            </form>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
            <h3 className="font-bold text-blue-900 mb-2">ℹ️ Informácie</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Každá ponuka je jednorazová (single-use)</li>
              <li>• Odkaz je platný iba do uvedeného dátumu</li>
              <li>• Po použití ponuky bude status zmenený na "used"</li>
              <li>• Individuálna ponuka Testovanie 9 je viazaná na konkrétneho používateľa a školský rok</li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  )
}

export default AdminCustomOffers
